/**
 * 预警统计分析页面
 */

import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Alert,
  Typography,
  Space,
  Statistic,
  Select,
  Empty,
  Spin,
  Button,
} from 'antd';
import {
  AlertOutlined,
  CloseCircleOutlined,
  TrophyOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList } from 'recharts';
import { WarningStats, BatchInfo } from '../../types';
import { getWarningStats, getInspectionBatches, exportWarningReport, getBatchReport } from '../../services/api';
import { formatNumber, formatDate, downloadFile, getCityLevelLabel } from '../../utils/helpers';
import { CHART_COLORS } from '../../utils/constants';
import { StatusCalculationEngine, StatusEnum } from '../../services/StatusCalculationEngine';

const { Title, Text } = Typography;
const { Option } = Select;

// 解析参考范围字符串
const parseReferenceRange = (referenceRange: string): { min?: number; max?: number } => {
  if (!referenceRange) return {};

  // 处理 "50-100" 格式
  const rangeMatch = referenceRange.match(/(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    return {
      min: parseFloat(rangeMatch[1]),
      max: parseFloat(rangeMatch[2])
    };
  }

  // 处理 "≤100" 或 "<=100" 格式
  const maxMatch = referenceRange.match(/[≤<=](\d+(?:\.\d+)?)/);
  if (maxMatch) {
    return { max: parseFloat(maxMatch[1]) };
  }

  // 处理 "≥50" 或 ">=50" 格式
  const minMatch = referenceRange.match(/[≥>=](\d+(?:\.\d+)?)/);
  if (minMatch) {
    return { min: parseFloat(minMatch[1]) };
  }

  return {};
};

// 计算项目状态
const calculateItemStatus = (item: any) => {
  const { min, max } = parseReferenceRange(item.reference_range);

  try {
    const result = StatusCalculationEngine.calculate({
      actualValue: item.actual_value,
      referenceMin: min,
      referenceMax: max,
      elementName: item.element_name,
      cityLevel: parseInt(item.city_level) || 5
    });
    return result;
  } catch (error) {
    console.warn('状态计算失败:', error, item);
    return {
      status: StatusEnum.NORMAL,
      color: '#52c41a',
      icon: '✓',
      text: '正常',
      description: '检查结果正常'
    };
  }
};

const WarningsAnalysis: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [batchLoading, setBatchLoading] = useState(true);
  const [batches, setBatches] = useState<BatchInfo[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<string>('all');
  const [warningStats, setWarningStats] = useState<WarningStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string>('all');
  const [allReportData, setAllReportData] = useState<any[]>([]);

  useEffect(() => {
    loadBatches();
  }, []);

  useEffect(() => {
    if (batches.length > 0) {
      loadWarningStats(selectedBatch === 'all' ? undefined : selectedBatch);
    }
  }, [selectedBatch, batches.length]);

  // 加载批次列表
  const loadBatches = async () => {
    try {
      setBatchLoading(true);
      const data = await getInspectionBatches();
      setBatches(data);

      // 批次加载完成后，立即加载预警统计，传递刚加载的批次数据
      if (data.length > 0) {
        await loadWarningStats(selectedBatch === 'all' ? undefined : selectedBatch, data);
      }
    } catch (err) {
      console.error('加载批次列表失败:', err);
      setError(err instanceof Error ? err.message : '加载批次列表失败');
    } finally {
      setBatchLoading(false);
    }
  };

  // 加载预警统计
  const loadWarningStats = async (batchId?: string, batchesList?: BatchInfo[]) => {
    try {
      setLoading(true);
      setError(null);
      console.log('正在加载预警统计，批次ID:', batchId);
      const data = await getWarningStats(batchId);
      console.log('预警统计数据:', data);
      setWarningStats(data);

      // 同时获取检查报告数据
      await loadReportData(batchId, batchesList || batches);
    } catch (err) {
      console.error('加载预警统计失败:', err);
      setError(err instanceof Error ? err.message : '加载预警统计失败');
      setWarningStats(null);
    } finally {
      setLoading(false);
    }
  };

  // 加载检查报告数据
  const loadReportData = async (batchId?: string, batchesList?: BatchInfo[]) => {
    try {
      const currentBatches = batchesList || batches;
      console.log('使用的批次列表:', currentBatches.length, '当前批次ID:', batchId);

      const targetBatches = batchId ? currentBatches.filter(b => b.batch_id === batchId) : currentBatches;
      console.log('目标批次数量:', targetBatches.length);

      const allReports: any[] = [];

      for (const batch of targetBatches) {
        try {
          console.log(`正在获取批次 ${batch.batch_id} 的报告数据`);
          const batchReport = await getBatchReport(batch.batch_id);
          console.log(`批次 ${batch.batch_id} 获取到 ${batchReport.reports.length} 条报告`);
          allReports.push(...batchReport.reports);
        } catch (error) {
          console.error(`获取批次 ${batch.batch_id} 报告失败:`, error);
        }
      }

      console.log('总共获取到报告数量:', allReports.length);
      setAllReportData(allReports);
    } catch (error) {
      console.error('加载检查报告数据失败:', error);
    }
  };

  // 处理批次选择
  const handleBatchChange = (batchId: string) => {
    setSelectedBatch(batchId);
  };

  // 刷新数据
  const handleRefresh = () => {
    loadWarningStats(selectedBatch === 'all' ? undefined : selectedBatch, batches);
  };

  // 导出预警报告
  const handleExportReport = async () => {
    try {
      const batchId = selectedBatch === 'all' ? undefined : selectedBatch;
      const blob = await exportWarningReport(batchId);
      const fileName = batchId 
        ? `预警报告_${batches.find(b => b.batch_id === batchId)?.company_name || batchId}_${new Date().toISOString().split('T')[0]}.xlsx`
        : `预警报告_全部批次_${new Date().toISOString().split('T')[0]}.xlsx`;
      downloadFile(blob, fileName);
    } catch (err) {
      console.error('导出预警报告失败:', err);
      // 如果后端没有实现导出接口，使用前端生成的方式
      generateFrontendExport();
    }
  };

  // 前端生成导出文件（备用方案）
  const generateFrontendExport = () => {
    if (!warningStats) return;
    
    // 创建Excel数据
    const data = [
      ['公司名称', '培训业务', '培训要素', '实际值', '参考范围', '风险等级'],
      ...dangerItems.map(item => [
        item.company_name,
        item.business_name,
        item.element_name,
        item.actual_value,
        item.reference_range,
        item.statusResult.text
      ]),
      ...warningItems.map(item => [
        item.company_name,
        item.business_name,
        item.element_name,
        item.actual_value,
        item.reference_range,
        item.statusResult.text
      ])
    ];

    // 转换为CSV格式
    const csvContent = data.map(row => row.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `预警报告_${selectedBatch === 'all' ? '全部批次' : selectedBatch}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadFile(blob, fileName);
  };

  if (loading && !warningStats) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>正在加载预警统计数据...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px 0' }}
        action={
          <Button size="small" onClick={handleRefresh}>
            重试
          </Button>
        }
      />
    );
  }

  // 如果没有数据，显示空状态
  if (!warningStats) {
    return (
      <div>
        <Title level={2} style={{ marginBottom: 24 }}>
          <AlertOutlined /> 预警统计分析
        </Title>
        
        {/* 工具栏 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space>
            <Text strong>筛选批次：</Text>
            <Select
              style={{ width: 300 }}
              value={selectedBatch}
              onChange={handleBatchChange}
              loading={batchLoading}
            >
              <Option value="all">全部批次</Option>
              {batches.map(batch => (
                <Option key={batch.batch_id} value={batch.batch_id}>
                  {formatDate(batch.import_date)} {batch.company_name}
                </Option>
              ))}
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled={true}
            >
              导出报告
            </Button>
          </Space>
        </Card>

        <Empty
          description={
            selectedBatch === 'all' 
              ? "暂无预警数据" 
              : `批次 ${batches.find(b => b.batch_id === selectedBatch)?.company_name || selectedBatch} 暂无预警数据`
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  // 处理公司筛选
  const handleCompanyChange = (company: string) => {
    setSelectedCompany(company);
  };

  // 使用检查报告数据和StatusCalculationEngine实时计算状态并分类数据
  const { dangerItems, warningItems, normalItems } = allReportData.length > 0 ? (() => {
    const danger: any[] = [];
    const warning: any[] = [];
    const normal: any[] = [];

    allReportData.forEach(report => {
      // 使用StatusCalculationEngine计算状态
      const { min, max } = parseReferenceRange(report.reference_range);
      let statusResult;
      try {
        statusResult = StatusCalculationEngine.calculate({
          actualValue: report.actual_value,
          referenceMin: min,
          referenceMax: max,
          elementName: report.element_name,
          cityLevel: report.city_level || 5
        });
      } catch (error) {
        statusResult = {
          status: StatusEnum.NORMAL,
          color: '#52c41a',
          icon: '✓',
          text: '正常',
          description: '检查结果正常'
        };
      }

      // 转换报告数据格式以匹配原有结构
      const item = {
        company_name: report.company_name,
        business_name: report.business_name,
        element_name: report.element_name,
        actual_value: report.actual_value,
        reference_range: report.reference_range,
        city_level: report.city_level,
        statusResult
      };

      if (statusResult.status === StatusEnum.DANGEROUS) {
        danger.push(item);
      } else if (statusResult.status === StatusEnum.SLIGHTLY_HIGH) {
        warning.push(item);
      } else {
        normal.push(item);
      }
    });

    return { dangerItems: danger, warningItems: warning, normalItems: normal };
  })() : { dangerItems: [], warningItems: [], normalItems: [] };

  // 获取所有可用公司（从风险项目中）
  const availableCompanies = Array.from(new Set([
    ...dangerItems.map(item => item.company_name),
    ...warningItems.map(item => item.company_name)
  ])).sort();

  // 筛选后的风险项目数据
  const allRiskItems = [...dangerItems, ...warningItems];
  const filteredDangerItems = selectedCompany === 'all'
    ? allRiskItems
    : allRiskItems.filter(item => item.company_name === selectedCompany);

  // 危险项目表格列
  const dangerColumns: ColumnsType<any> = [
    {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 200,
      ellipsis: true,
    },
    {
      title: '培训业务',
      dataIndex: 'business_name',
      key: 'business_name',
      width: 150,
      ellipsis: true,
    },
    {
      title: '培训要素',
      dataIndex: 'element_name',
      key: 'element_name',
      width: 150,
      ellipsis: true,
    },
    {
      title: '实际值',
      dataIndex: 'actual_value',
      key: 'actual_value',
      width: 100,
      align: 'right',
      render: (value: number) => formatNumber(value),
    },
    {
      title: '参考范围',
      dataIndex: 'reference_range',
      key: 'reference_range',
      width: 150,
      ellipsis: true,
    },
    {
      title: '风险等级',
      key: 'risk_level',
      width: 100,
      render: (_, record) => {
        const status = record.statusResult;
        if (status.status === StatusEnum.DANGEROUS) {
          return (
            <Tag color="error" icon={<CloseCircleOutlined />}>
              {status.text}
            </Tag>
          );
        } else if (status.status === StatusEnum.SLIGHTLY_HIGH) {
          return (
            <Tag color="warning" icon={<AlertOutlined />}>
              {status.text}
            </Tag>
          );
        } else {
          return (
            <Tag color="success" icon={status.icon}>
              {status.text}
            </Tag>
          );
        }
      },
    },
  ];


  // 问题分布数据（预留，如需要可以用于其他图表）
  // const issueDistributionData = warningStats.top_issues.map((issue, index) => ({
  //   name: issue.issue_type,
  //   value: issue.count,
  //   color: CHART_COLORS[index % CHART_COLORS.length],
  // }));

  // 风险等级分布数据
  const riskLevelData = [
    {
      name: '严重危险',
      value: dangerItems.length,
      color: '#f5222d',
    },
    {
      name: '预警提醒',
      value: warningItems.length,
      color: '#fa8c16',
    },
  ].filter(item => item.value > 0);

  // 公司问题分布统计
  const regionData = (() => {
    const companyDistribution: Record<string, number> = {};

    // 统计危险项目中的公司
    dangerItems.forEach(item => {
      companyDistribution[item.company_name] = (companyDistribution[item.company_name] || 0) + 1;
    });

    // 统计预警项目中的公司
    warningItems.forEach(item => {
      companyDistribution[item.company_name] = (companyDistribution[item.company_name] || 0) + 1;
    });

    return Object.entries(companyDistribution)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // 只显示前10名
  })();

  return (
    <div>
      <Title level={2} style={{ marginBottom: 24 }}>
        <AlertOutlined /> 预警统计分析
      </Title>

      {/* 工具栏 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space>
          <Text strong>筛选批次：</Text>
          <Select
            style={{ width: 300 }}
            value={selectedBatch}
            onChange={handleBatchChange}
            loading={batchLoading}
          >
            <Option value="all">全部批次</Option>
            {batches.map(batch => (
              <Option key={batch.batch_id} value={batch.batch_id}>
                {formatDate(batch.import_date)} {batch.company_name}
              </Option>
            ))}
          </Select>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            刷新
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExportReport}
            disabled={!warningStats || (dangerItems.length === 0 && warningItems.length === 0)}
          >
            导出报告
          </Button>
        </Space>
      </Card>

      {/* 统计概览 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="风险项目总数"
              value={dangerItems.length + warningItems.length}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
              suffix="项"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="严重危险项目"
              value={dangerItems.length}
              prefix={<AlertOutlined />}
              valueStyle={{ color: '#722ed1' }}
              suffix="项"
            />
          </Card>
        </Col>
      </Row>

      {/* 图表分析 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* 问题项目地区分布 */}
        <Col xs={24} lg={12}>
          <Card title="问题项目地区分布" style={{ height: 400 }}>
            {(() => {
              // 使用检查报告数据按城市等级统计超标问题
              const regionProblemStats = allReportData.length > 0 ? (() => {
                const stats: { [key: string]: { total: number; problems: number } } = {};

                allReportData.forEach(report => {
                  const cityLevel = getCityLevelLabel(report.city_level || 5);

                  // 使用StatusCalculationEngine计算状态
                  const { min, max } = parseReferenceRange(report.reference_range);
                  let statusResult;
                  try {
                    statusResult = StatusCalculationEngine.calculate({
                      actualValue: report.actual_value,
                      referenceMin: min,
                      referenceMax: max,
                      elementName: report.element_name,
                      cityLevel: report.city_level || 5
                    });
                  } catch (error) {
                    statusResult = {
                      status: StatusEnum.NORMAL,
                      color: '#52c41a',
                      icon: '✓',
                      text: '正常',
                      description: '检查结果正常'
                    };
                  }

                  if (!stats[cityLevel]) {
                    stats[cityLevel] = { total: 0, problems: 0 };
                  }

                  stats[cityLevel].total++;

                  // 只统计超标的问题（预警和危险）
                  if (statusResult.status === StatusEnum.SLIGHTLY_HIGH || statusResult.status === StatusEnum.DANGEROUS) {
                    stats[cityLevel].problems++;
                  }
                });

                // 转换为图表数据，计算比例
                return Object.entries(stats)
                  .map(([region, data], index) => ({
                    name: region,
                    value: data.problems,
                    total: data.total,
                    percentage: data.total > 0 ? ((data.problems / data.total) * 100).toFixed(1) : '0'
                  }))
                  .filter(item => item.value > 0); // 只显示有问题的地区
              })() : [];

              return regionProblemStats.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={regionProblemStats}
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      paddingAngle={5}
                      dataKey="value"
                      label={(entry: any) => `${entry.percentage}%`}
                      labelLine={false}
                    >
                      {regionProblemStats.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [
                        [`${value} 个问题项目`, '问题数量'],
                        [`${props.payload.percentage}%`, '问题比例'],
                        [`${props.payload.total} 个总项目`, '总数量']
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="暂无问题项目数据" />
              );
            })()}
          </Card>
        </Col>

        {/* 公司问题分布 */}
        <Col xs={24} lg={12}>
          <Card title="公司问题分布" style={{ height: 400 }}>
            {regionData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={regionData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value} 项`, '问题数量']} />
                  <Bar dataKey="count" fill="#722ed1">
                    <LabelList dataKey="count" position="top" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="暂无公司分布数据" />
            )}
          </Card>
        </Col>
      </Row>

      {/* 风险项目列表 */}
      <Card
        title={
          <Space>
            <CloseCircleOutlined style={{ color: '#f5222d' }} />
            <Text strong>风险项目列表</Text>
            <Tag color="error">{filteredDangerItems.length} 项</Tag>
          </Space>
        }
        extra={
          <Space>
            <Text>公司筛选：</Text>
            <Select
              style={{ width: 200 }}
              value={selectedCompany}
              onChange={handleCompanyChange}
              showSearch
              placeholder="选择公司"
            >
              <Option value="all">全部公司</Option>
              {availableCompanies.map(company => (
                <Option key={company} value={company}>
                  {company}
                </Option>
              ))}
            </Select>
          </Space>
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Table
          columns={dangerColumns}
          dataSource={filteredDangerItems}
          rowKey={(record, index) => `danger-${index}`}
          size="small"
          scroll={{ x: 800 }}
          pagination={{
            pageSize: 10,
            size: 'small',
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </Card>

      {/* 问题类型统计 */}
      {(dangerItems.length > 0 || warningItems.length > 0) && (
        <Card
          title={
            <Space>
              <TrophyOutlined />
              <Text strong>问题类型统计</Text>
            </Space>
          }
          style={{ marginTop: 16 }}
        >
          <Row gutter={[16, 16]}>
            {dangerItems.length > 0 && (
              <Col xs={24} sm={12} md={8} lg={6}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="严重危险"
                    value={dangerItems.length}
                    valueStyle={{
                      color: '#f5222d',
                      fontSize: '24px'
                    }}
                    suffix="项"
                  />
                </Card>
              </Col>
            )}
            {warningItems.length > 0 && (
              <Col xs={24} sm={12} md={8} lg={6}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="预警提醒"
                    value={warningItems.length}
                    valueStyle={{
                      color: '#fa8c16',
                      fontSize: '24px'
                    }}
                    suffix="项"
                  />
                </Card>
              </Col>
            )}
          </Row>
        </Card>
      )}
    </div>
  );
};

export default WarningsAnalysis;